// Generated by Copilot
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { PACKAGING_FORMAT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PackagingFormat } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    packagingFormat: PackagingFormat.DetailsResponse;
}

export const PackagingFormatDetailsCard = ({ packagingFormat }: Props) => {
    const { ...rest } = packagingFormat;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<PackagingFormat.UpdateRequest>({
        resolver: yupResolver(PACKAGING_FORMAT_SCHEMA.omit(['id'])),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['packagingFormat', packagingFormat.id],
        mutationFn: (data: PackagingFormat.UpdateRequest) =>
            Api.Servings.PackagingFormat.putPackagingFormat(
                packagingFormat.id,
                data
            ),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: PackagingFormat.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated packaging format!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error updating packaging format!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Packaging Format Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <CheckboxElement
                            label="Single serve"
                            name="isSingleServe"
                            disabled={!editable}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="PackagingFormat">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
